import type {
  AdminLearningContentRow,
  ProgramFixedContentRowUpsert,
  ProgramScopedContentRow,
} from '../types/domain';
import {
  getAdminLearningContentRows,
  getAdminLearningContentTemplates,
} from '../services/adminLearningContentService';
import { getProgramScopedContentRows } from '../services/programScopedContentService';
import {
  getProgramWorkspaceScopeEntry,
  resolveProgramWorkspacePersistKind,
  upsertProgramWorkspaceScopeEntry,
} from './programEditWorkspace';
import { saveProgramWorkspaceScopes } from './programUnifiedSave';
import { cellLearningTextForEditor, readProgramLearningContentFields } from './programLearningContentFields';
import {
  mergeProgramBaselineWithAdminLearningContent,
  programAlignedImportColumnsHaveData,
} from './programLearningContentImport';
import type { TrackProgramMode } from './trackProgramModeStorage';

export type SchoolGradeNumber = 1 | 2 | 3 | 4 | 5 | 6;

export type ProgramLearningBatchGenerateTarget = {
  gradeId: string;
  gradeLabel: string;
  trackName: string;
  templateName: string;
  publisher: string;
  contentGradeNumber?: SchoolGradeNumber;
  aliases: string[];
  programMode: TrackProgramMode;
  syncRepAlias: string;
};

export function asSchoolGradeNumber(value: unknown): SchoolGradeNumber | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (n === 1 || n === 2 || n === 3 || n === 4 || n === 5 || n === 6) return n;
  return undefined;
}

export function normalizeSchoolGradeNumbers(values: readonly number[]): SchoolGradeNumber[] {
  const next = Array.from(
    new Set(values.map((n) => asSchoolGradeNumber(n)).filter((n): n is SchoolGradeNumber => n != null))
  ).sort((a, b) => a - b);
  return next.length ? next : [1];
}

export function defaultSchoolGradeNumber(
  options: readonly SchoolGradeNumber[],
  preferred?: number
): SchoolGradeNumber {
  const preferredGrade = asSchoolGradeNumber(preferred);
  if (preferredGrade && options.includes(preferredGrade)) return preferredGrade;
  return options[0] ?? 1;
}

export type TrackLearningSourceSelection = {
  templateName: string;
  publisher: string;
  gradeNumber?: SchoolGradeNumber;
};

export function parseTrackLearningSourceSelection(
  raw: string | undefined,
  fallbackTemplateName = ''
): TrackLearningSourceSelection {
  const value = (raw ?? '').trim();
  if (!value) return { templateName: '', publisher: '' };
  if (value.startsWith('{')) {
    try {
      const parsed = JSON.parse(value) as {
        templateName?: unknown;
        publisher?: unknown;
        gradeNumber?: unknown;
      };
      const publisher = typeof parsed.publisher === 'string' ? parsed.publisher.trim() : '';
      const templateName = typeof parsed.templateName === 'string' ? parsed.templateName.trim() : '';
      const gradeNumber = asSchoolGradeNumber(parsed.gradeNumber);
      if (publisher) return { templateName, publisher, ...(gradeNumber ? { gradeNumber } : {}) };
    } catch {
      /* 예전 출판사만 저장한 값 */
    }
  }
  return { templateName: fallbackTemplateName.trim(), publisher: value };
}

export function serializeTrackLearningSourceSelection(
  selection: TrackLearningSourceSelection
): string {
  const publisher = selection.publisher.trim();
  if (!publisher) return '';
  const gradeNumber = asSchoolGradeNumber(selection.gradeNumber);
  return JSON.stringify({
    templateName: selection.templateName.trim(),
    publisher,
    ...(gradeNumber ? { gradeNumber } : {}),
  });
}

export function formatTrackLearningSourceLines(
  raw: string | undefined,
  fallbackTemplateName = ''
): { headline: string; publisher: string } | null {
  const selection = parseTrackLearningSourceSelection(raw, fallbackTemplateName);
  if (!selection.publisher) return null;
  const gradeLabel = selection.gradeNumber ? `${selection.gradeNumber}학년` : '';
  const templateName = selection.templateName.trim();
  const showTemplate = Boolean(templateName && templateName !== fallbackTemplateName.trim());
  const headline = [gradeLabel, showTemplate ? templateName : ''].filter(Boolean).join('.');
  return { headline, publisher: selection.publisher };
}

export function formatTrackLearningSourceLabel(
  raw: string | undefined,
  fallbackTemplateName = ''
): string {
  const lines = formatTrackLearningSourceLines(raw, fallbackTemplateName);
  if (!lines) return '';
  return [lines.headline, lines.publisher].filter(Boolean).join(' · ');
}

export type ProgramLearningBatchGenerateItemKind = 'created' | 'skipped-existing' | 'skipped-empty';

export type ProgramLearningBatchGenerateItem = {
  kind: ProgramLearningBatchGenerateItemKind;
  gradeLabel: string;
  trackName: string;
  termNumber: 1 | 2;
  termLabel: string;
  publisher: string;
  templateName: string;
  scopeLabel: string;
};

export function formatProgramLearningBatchTermProgramLabel(termNumber: 1 | 2): string {
  return `${termNumber}학기 프로그램`;
}

export type ProgramLearningBatchGenerateResult = {
  created: ProgramLearningBatchGenerateItem[];
  skippedExisting: ProgramLearningBatchGenerateItem[];
  skippedEmpty: ProgramLearningBatchGenerateItem[];
};

function scopeKeyDisplayLabel(scopeKey: string): string {
  const sep = scopeKey.indexOf('|');
  const alias = sep >= 0 ? scopeKey.slice(sep + 1).trim() : '';
  if (!alias || alias === '-') return '';
  return alias;
}

export function formatProgramLearningBatchItemTitle(item: ProgramLearningBatchGenerateItem): string {
  const parts = [item.gradeLabel, item.trackName];
  if (item.scopeLabel) parts.push(item.scopeLabel);
  return parts.filter(Boolean).join(' · ');
}

export function formatProgramLearningBatchItemMeta(item: ProgramLearningBatchGenerateItem): string {
  const template = item.templateName.trim();
  const publisher = item.publisher.trim();
  const trackName = item.trackName.trim();
  if (template && publisher && template !== trackName) {
    return `${template} · ${publisher}`;
  }
  return publisher;
}

function makeBatchItem(
  kind: ProgramLearningBatchGenerateItemKind,
  target: ProgramLearningBatchGenerateTarget,
  termNumber: 1 | 2,
  scopeKey = ''
): ProgramLearningBatchGenerateItem {
  return {
    kind,
    gradeLabel: target.gradeLabel,
    trackName: target.trackName,
    termNumber,
    termLabel: formatProgramLearningBatchTermProgramLabel(termNumber),
    publisher: target.publisher,
    templateName: target.templateName,
    scopeLabel: scopeKeyDisplayLabel(scopeKey),
  };
}

function filterRowsByPublisher(
  rows: AdminLearningContentRow[],
  publisher: string
): AdminLearningContentRow[] {
  const pub = publisher.trim();
  if (!pub) return [];
  return rows.filter((row) => (row.publisher ?? '').trim() === pub);
}

/** term1/term2가 없으면 common을 1학기에만 쓴다. */
export function resolveAdminLearningRowsByProgramTerm(params: {
  term1: AdminLearningContentRow[];
  term2: AdminLearningContentRow[];
  common: AdminLearningContentRow[];
  publisher: string;
}): { term1: AdminLearningContentRow[]; term2: AdminLearningContentRow[] } {
  const term1 = filterRowsByPublisher(params.term1, params.publisher);
  const term2 = filterRowsByPublisher(params.term2, params.publisher);
  const common = filterRowsByPublisher(params.common, params.publisher);
  if (term1.length === 0 && term2.length === 0 && common.length > 0) {
    return { term1: common, term2: [] };
  }
  return { term1, term2 };
}

export async function listAdminLearningTemplateNamesForGrade(params: {
  workspaceId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
}): Promise<string[]> {
  const [term1, term2, common] = await Promise.all([
    getAdminLearningContentTemplates(params.workspaceId, params.gradeNumber, 'term1').catch(() => []),
    getAdminLearningContentTemplates(params.workspaceId, params.gradeNumber, 'term2').catch(() => []),
    getAdminLearningContentTemplates(params.workspaceId, params.gradeNumber, 'common').catch(() => []),
  ]);
  const names = new Map<string, string>();
  for (const row of [...term1, ...term2, ...common]) {
    const name = (row.template_name ?? '').trim();
    if (!name) continue;
    names.set(name, name);
  }
  return Array.from(names.values()).sort((a, b) => a.localeCompare(b, 'ko'));
}

export async function listAdminLearningPublishersForTrack(params: {
  workspaceId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  trackName: string;
}): Promise<string[]> {
  const templateName = params.trackName.trim();
  if (!templateName) return [];
  const [term1, term2, common] = await Promise.all([
    getAdminLearningContentRows(params.workspaceId, params.gradeNumber, 'term1', templateName).catch(() => []),
    getAdminLearningContentRows(params.workspaceId, params.gradeNumber, 'term2', templateName).catch(() => []),
    getAdminLearningContentRows(params.workspaceId, params.gradeNumber, 'common', templateName).catch(() => []),
  ]);
  const map = new Map<string, string>();
  for (const row of [...term1, ...term2, ...common]) {
    const publisher = (row.publisher ?? '').trim();
    if (!publisher) continue;
    map.set(publisher, publisher);
  }
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'ko'));
}

function scopedRowToWorkspaceUpsert(row: ProgramScopedContentRow): ProgramFixedContentRowUpsert {
  const learningFields = readProgramLearningContentFields(row);
  return {
    id: row.id,
    workspace_id: row.workspace_id,
    grade_id: row.grade_id,
    academic_year: row.academic_year,
    term_number: row.term_number,
    order_index: row.order_index,
    source_annual_cell_key: row.source_annual_cell_key ?? null,
    event_name: null,
    timetable_text: row.timetable_text ?? '',
    unit_text: row.unit_text ?? '',
    learning_text: cellLearningTextForEditor(row),
    learning_note: learningFields.note,
    presentation_ref_id: learningFields.presentationRefId,
    worksheet_ref_id: learningFields.worksheetRefId,
    page_text: row.page_text ?? '',
    assistant_page_text: row.assistant_page_text ?? '',
    current_period_text: row.current_period_text ?? '',
    total_period_text: row.total_period_text ?? '',
    material_text: row.material_text ?? '',
    record_cross_curricular_text: row.record_cross_curricular_text ?? '',
    record_memo_text: row.record_memo_text ?? '',
    record_evaluation_text: row.record_evaluation_text ?? '',
    record_class_text: row.record_class_text ?? '',
    record_member_group_id: row.record_member_group_id ?? null,
    bg_color_by_field: { ...(row.bg_color_by_field ?? {}) },
  };
}

export function resolveTrackProgramLearningScopeKey(params: {
  trackName: string;
  aliases: string[];
  programMode: TrackProgramMode;
  syncRepAlias: string;
}): string {
  return trackProgramScopeKeys(params)[0]?.scopeKey ?? `${params.trackName.trim()}|-`;
}

function trackProgramScopeKeys(params: {
  trackName: string;
  aliases: string[];
  programMode: TrackProgramMode;
  syncRepAlias: string;
}): { scopeKey: string; fanoutScopeKeys?: string[] }[] {
  const trackName = params.trackName.trim();
  const aliases = params.aliases.map((alias) => alias.trim()).filter(Boolean);
  if (params.programMode === 'unified' || aliases.length < 2) {
    return [{ scopeKey: `${trackName}|-` }];
  }
  if (params.programMode === 'sync') {
    const rep = (params.syncRepAlias || aliases[0] || '').trim() || aliases[0];
    return [{ scopeKey: `${trackName}|${rep}` }];
  }
  const defaultAlias = aliases[0];
  return [{ scopeKey: `${trackName}|${defaultAlias}` }];
}

async function applyAdminRowsToTrackScope(params: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  trackName: string;
  scopeKey: string;
  fanoutScopeKeys?: string[];
  adminRows: AdminLearningContentRow[];
}): Promise<{ status: 'applied' | 'skipped-existing' | 'skipped-empty'; scopeId?: string }> {
  if (!params.adminRows.length) return { status: 'skipped-empty' };
  const existing = await getProgramScopedContentRows(
    params.workspaceId,
    params.academicYear,
    params.termNumber,
    params.gradeId,
    'track',
    params.scopeKey,
    { forceRefresh: true }
  );
  const baseline = [...existing]
    .sort((a, b) => a.order_index - b.order_index)
    .map(scopedRowToWorkspaceUpsert);
  const scopeId = `track|${params.workspaceId}|${params.gradeId}|${params.academicYear}|${params.termNumber}|${params.scopeKey}`;
  const dirtyEntry = getProgramWorkspaceScopeEntry(scopeId);
  const dirtyRows = dirtyEntry?.isDirty ? dirtyEntry.rows ?? [] : [];
  if (programAlignedImportColumnsHaveData(baseline) || programAlignedImportColumnsHaveData(dirtyRows)) {
    return { status: 'skipped-existing' };
  }
  const merged = mergeProgramBaselineWithAdminLearningContent(baseline, params.adminRows, {
    workspaceId: params.workspaceId,
    gradeId: params.gradeId,
    academicYear: params.academicYear,
    termNumber: params.termNumber,
  });
  if (!merged.length) return { status: 'skipped-empty' };
  const persistKind = resolveProgramWorkspacePersistKind(undefined, merged.length, baseline.length);
  const fanoutKeys = params.fanoutScopeKeys?.filter((key) => key !== params.scopeKey) ?? [];
  upsertProgramWorkspaceScopeEntry({
    scopeId,
    scopeType: 'track',
    scopeKey: params.scopeKey,
    workspaceId: params.workspaceId,
    gradeId: params.gradeId,
    academicYear: params.academicYear,
    termNumber: params.termNumber,
    rows: merged,
    isDirty: true,
    updatedAt: Date.now(),
    persistKind,
    ...(fanoutKeys.length
      ? {
          trackSyncFanoutScopeKeys: [params.scopeKey, ...fanoutKeys],
          trackSyncScheduleAnnualKeysByScope: Object.fromEntries(
            [params.scopeKey, ...fanoutKeys].map((key) => [key, [] as string[]])
          ),
        }
      : {}),
  });
  return { status: 'applied', scopeId };
}

export async function generateProgramLearningFromSelectedPublishers(params: {
  workspaceId: string;
  academicYearByGradeId: Record<string, number>;
  targets: ProgramLearningBatchGenerateTarget[];
  gradeNumberByGradeId: Record<string, 1 | 2 | 3 | 4 | 5 | 6>;
}): Promise<ProgramLearningBatchGenerateResult> {
  const result: ProgramLearningBatchGenerateResult = {
    created: [],
    skippedExisting: [],
    skippedEmpty: [],
  };
  const appliedScopeIds: string[] = [];

  for (const target of params.targets) {
    const trackName = target.trackName.trim();
    const templateName = (target.templateName || trackName).trim();
    const publisher = target.publisher.trim();
    const gradeNumber = target.contentGradeNumber ?? params.gradeNumberByGradeId[target.gradeId];
    const academicYear = params.academicYearByGradeId[target.gradeId];
    if (!trackName || !templateName || !publisher || !gradeNumber || !academicYear) continue;

    const [term1Rows, term2Rows, commonRows] = await Promise.all([
      getAdminLearningContentRows(params.workspaceId, gradeNumber, 'term1', templateName).catch(() => []),
      getAdminLearningContentRows(params.workspaceId, gradeNumber, 'term2', templateName).catch(() => []),
      getAdminLearningContentRows(params.workspaceId, gradeNumber, 'common', templateName).catch(() => []),
    ]);
    const byTerm = resolveAdminLearningRowsByProgramTerm({
      term1: term1Rows,
      term2: term2Rows,
      common: commonRows,
      publisher,
    });
    const termJobs: Array<{ termNumber: 1 | 2; rows: AdminLearningContentRow[] }> = [
      { termNumber: 1, rows: byTerm.term1 },
      { termNumber: 2, rows: byTerm.term2 },
    ];

    for (const job of termJobs) {
      if (!job.rows.length) {
        if (job.termNumber === 1 && !byTerm.term2.length) {
          result.skippedEmpty.push(makeBatchItem('skipped-empty', target, 1));
        }
        continue;
      }
      const scopes = trackProgramScopeKeys(target);
      for (const scope of scopes) {
        const applied = await applyAdminRowsToTrackScope({
          workspaceId: params.workspaceId,
          gradeId: target.gradeId,
          academicYear,
          termNumber: job.termNumber,
          trackName,
          scopeKey: scope.scopeKey,
          fanoutScopeKeys: scope.fanoutScopeKeys,
          adminRows: job.rows,
        });
        if (applied.status === 'applied') {
          result.created.push(makeBatchItem('created', target, job.termNumber, scope.scopeKey));
          if (applied.scopeId) appliedScopeIds.push(applied.scopeId);
        } else if (applied.status === 'skipped-existing') {
          result.skippedExisting.push(
            makeBatchItem('skipped-existing', target, job.termNumber, scope.scopeKey)
          );
        } else {
          result.skippedEmpty.push(makeBatchItem('skipped-empty', target, job.termNumber, scope.scopeKey));
        }
      }
    }
  }

  if (appliedScopeIds.length) {
    await saveProgramWorkspaceScopes(appliedScopeIds);
  }

  return result;
}
